import { Link, useLocation } from 'react-router-dom';
import { useContext, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  FaHome,
  FaFileContract,
  FaUsersCog,
  FaInfoCircle,
  FaChartLine,
  FaGavel,
  FaTrashAlt,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaUsers,
  FaVirus,
  FaComments,
  FaBook,
  FaChartBar,
  FaCog,
  FaBroadcastTower,
  FaRobot,
  FaCommentDots,
  FaClipboardList,
  FaSeedling,
  FaFlask,
  FaLeaf,
  FaProjectDiagram,
} from 'react-icons/fa';
import { MdSubscriptions } from 'react-icons/md';

// Grouped nav config. Each group renders under a small uppercase label when
// the sidebar is expanded; groups are invisible structure only (no extra
// markup) when collapsed to icon-only mode.
const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { path: '/', label: 'Dashboard', icon: <FaHome /> },
      { path: '/admin/monitoring', label: 'Monitoring', icon: <FaBroadcastTower /> },
    ],
  },
  {
    label: 'Farm Management',
    items: [
      {
        path: '/users',
        label: 'Users',
        icon: <FaUsers />,
        children: [
          { path: '/users', label: 'Users' },
          { path: '/users/pending-verifications', label: 'Pending Verifications' },
        ],
      },
      { path: '/diseases', label: 'Diseases', icon: <FaVirus /> },
      { path: '/consultations', label: 'Consultations', icon: <FaComments /> },
      { path: '/waste', label: 'Waste', icon: <FaTrashAlt /> },
      { path: '/bids', label: 'Bids', icon: <FaGavel /> },
      { path: '/manage-subscription-plans', label: 'Subscription Plans', icon: <MdSubscriptions /> },
      { path: '/manage-users-subscriptions', label: 'Users Subscriptions', icon: <FaUsersCog /> },
    ],
  },
  {
    label: 'AI & Evidence',
    items: [
      { path: '/usage-reports', label: 'Usage Reports', icon: <FaRobot /> },
      { path: '/ai-capabilities', label: 'AI Capabilities', icon: <FaClipboardList /> },
      { path: '/feedback', label: 'Feedback & Testimonials', icon: <FaCommentDots /> },
      { path: '/production-records', label: 'Production Records', icon: <FaSeedling /> },
      { path: '/productivity-forecast', label: 'Productivity Forecast', icon: <FaProjectDiagram /> },
      { path: '/trial-deployments', label: 'Trial Deployments', icon: <FaFlask /> },
      { path: '/research-archive', label: 'Research Archive', icon: <FaBook /> },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { path: '/analytics', label: 'Analytics', icon: <FaChartLine /> },
      { path: '/admin/waste-analytics', label: 'Waste Analytics', icon: <FaLeaf /> },
    ],
  },
  {
    label: 'Content & Policies',
    items: [
      {
        path: '/content',
        label: 'Content',
        icon: <FaBook />,
        children: [
          { path: '/content', label: 'Content' },
          { path: '/categories', label: 'Categories' },
        ],
      },
      { path: '/admin/privacy-policy', label: 'Privacy Policy', icon: <FaChartBar /> },
      { path: '/admin/term-and-conditions', label: 'Terms & Conditions', icon: <FaFileContract /> },
      { path: '/admin/about-us', label: 'About Us', icon: <FaInfoCircle /> },
    ],
  },
  {
    label: 'System',
    items: [{ path: '/settings', label: 'Settings', icon: <FaCog /> }],
  },
];

function isActivePath(pathname, itemPath) {
  if (itemPath === '/') return pathname === '/';
  return pathname === itemPath || pathname.startsWith(itemPath + '/');
}

function AdminSidebar({ isOpen, toggleSidebar }) {
  const [openSubmenus, setOpenSubmenus] = useState({});
  const { pathname } = useLocation();
  const { user } = useContext(AuthContext) || {};

  const toggleSubmenu = (label) => {
    setOpenSubmenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const closeOnMobile = () => window.innerWidth < 1024 && isOpen && toggleSidebar();

  return (
    <>
      <style>{`
        .admin-sidebar-scroll { scrollbar-width: thin; scrollbar-color: #cbd5c9 transparent; }
        .admin-sidebar-scroll::-webkit-scrollbar { width: 5px; }
        .admin-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .admin-sidebar-scroll::-webkit-scrollbar-thumb { background-color: #cbd5c9; border-radius: 999px; }
        .admin-sidebar-scroll::-webkit-scrollbar-thumb:hover { background-color: #9fb39c; }
      `}</style>

      <aside
        className={`fixed inset-y-0 left-0 bg-white dark:bg-gray-800 border-r border-gray-100 dark:border-gray-700 transition-all duration-300 ease-in-out z-20 shadow-sm flex flex-col ${
          isOpen ? 'w-64' : 'w-0 lg:w-16'
        } ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Brand header */}
        <div className={`h-16 flex items-center border-b border-gray-100 dark:border-gray-700 shrink-0 ${isOpen ? 'px-4 justify-between' : 'px-0 justify-center'}`}>
          <Link to="/" className="flex items-center gap-2.5 min-w-0" onClick={closeOnMobile}>
            <span className="w-9 h-9 rounded-lg bg-primary text-white flex items-center justify-center font-bold text-base shrink-0">
              CF
            </span>
            {isOpen && (
              <span className="text-[15px] font-semibold text-gray-800 dark:text-white truncate">
                Chick Farm <span className="text-gray-400 font-normal">Admin</span>
              </span>
            )}
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 admin-sidebar-scroll">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mb-1">
              {isOpen && (
                <div className="px-4 pt-4 pb-1.5 text-[10px] font-semibold tracking-wider text-gray-400 uppercase select-none">
                  {group.label}
                </div>
              )}
              {!isOpen && <div className="mx-3 my-2 border-t border-gray-100 dark:border-gray-700 first:mt-0" />}

              {group.items.map((item) => {
                const hasChildren = item.children?.length > 0;
                const isOpenSubmenu = openSubmenus[item.label];
                const active = hasChildren
                  ? item.children.some((c) => isActivePath(pathname, c.path))
                  : isActivePath(pathname, item.path);

                return (
                  <div key={item.path}>
                    {hasChildren ? (
                      <button
                        type="button"
                        className={`group relative w-full flex items-center justify-between px-4 py-2.5 text-[13.5px] font-medium transition-colors duration-150 focus:outline-none ${
                          active
                            ? 'text-primary bg-primary/5'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-white'
                        } ${!isOpen ? 'justify-center px-0' : ''}`}
                        onClick={() => toggleSubmenu(item.label)}
                        title={!isOpen ? item.label : undefined}
                      >
                        {active && <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r bg-primary" />}
                        <div className={`flex items-center min-w-0 ${!isOpen ? 'justify-center' : ''}`}>
                          <span className={`text-[15px] shrink-0 ${isOpen ? 'mr-3' : ''}`}>{item.icon}</span>
                          {isOpen && <span className="truncate">{item.label}</span>}
                        </div>
                        {isOpen && (
                          <FaChevronDown className={`text-xs text-gray-400 transition-transform duration-200 shrink-0 ${isOpenSubmenu ? 'rotate-180' : 'rotate-0'}`} />
                        )}
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className={`group relative w-full flex items-center px-4 py-2.5 text-[13.5px] font-medium transition-colors duration-150 ${
                          active
                            ? 'text-primary bg-primary/5'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-white'
                        } ${!isOpen ? 'justify-center px-0' : ''}`}
                        onClick={closeOnMobile}
                        title={!isOpen ? item.label : undefined}
                      >
                        {active && <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r bg-primary" />}
                        <span className={`text-[15px] shrink-0 ${isOpen ? 'mr-3' : ''}`}>{item.icon}</span>
                        {isOpen && <span className="truncate">{item.label}</span>}
                      </Link>
                    )}
                    {hasChildren && isOpen && isOpenSubmenu && (
                      <div className="ml-[3.1rem] border-l border-gray-100 dark:border-gray-700 pl-3 my-0.5">
                        {item.children.map((child) => {
                          const childActive = isActivePath(pathname, child.path);
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`block px-2 py-1.5 text-[13px] rounded-md transition-colors duration-150 ${
                                childActive
                                  ? 'text-primary font-medium'
                                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white'
                              }`}
                              onClick={closeOnMobile}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer: user + collapse toggle (desktop only) */}
        <div className="shrink-0 border-t border-gray-100 dark:border-gray-700">
          {isOpen && user && (
            <div className="px-4 py-3 flex items-center gap-2.5 min-w-0">
              <span className="w-8 h-8 rounded-full bg-secondary/20 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                {(user.firstName?.[0] || user.email?.[0] || 'A').toUpperCase()}
              </span>
              <div className="min-w-0">
                <div className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                  {user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.email}
                </div>
                <div className="text-[10px] text-gray-400 capitalize">{user.role || 'admin'}</div>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={toggleSidebar}
            className="hidden lg:flex w-full items-center justify-center gap-2 py-2.5 text-xs text-gray-400 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors border-t border-gray-100 dark:border-gray-700"
            title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isOpen ? <FaChevronLeft /> : <FaChevronRight />}
            {isOpen && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-10 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
}

export default AdminSidebar;
